/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { objNamesToSnakeCase } from "common/utils/helpers/cases";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";

type BaseAnalyticsProps = {
  artworkId: string;
  sessionId: string;
};

type PinAnalyticsProps = BaseAnalyticsProps & {
  pinId: string;
};

type SessionCountProps = PinAnalyticsProps & {
  userMessagesCount: number;
  aiMessagesCount: number;
};

type EscapeHatchAnalyticsProps = {
  triggerType: string;
  pinCount: number;
  messageCount: number;
};

const buildPayload = (eventProps: Record<string, unknown>): Record<string, unknown> =>
  objNamesToSnakeCase({
    timestamp: Date.now(),
    ...eventProps
  }) as Record<string, unknown>;

export const trackTransfersPinsCtaClicked = ({
  artworkId,
  sessionId,
  userType
}: {
  artworkId: string;
  sessionId?: string;
  userType?: string;
}): void => {
  submitDtfEvent(
    "transfers_pins_cta_clicked",
    buildPayload({
      artworkId,
      ...(sessionId && { sessionId }),
      ...(userType && { userType })
    })
  );
};

export const trackPinsSessionSummaryEditClicked = ({
  artworkId,
  sessionId
}: BaseAnalyticsProps): void => {
  submitDtfEvent(
    "pins_session_summary_edit_clicked",
    buildPayload({
      artworkId,
      sessionId
    })
  );
};

export const trackTransfersPinningInstructionsRemoved = ({
  artworkId,
  sessionId
}: BaseAnalyticsProps): void => {
  submitDtfEvent(
    "transfers_pinning_instructions_removed",
    buildPayload({
      artworkId,
      sessionId
    })
  );
};

export const trackTransfersPinningModeEntered = ({
  artworkId,
  sessionId,
  hasExistingPins,
  initialMessage
}: BaseAnalyticsProps & {
  hasExistingPins: boolean;
  initialMessage?: string;
}): void => {
  submitDtfEvent(
    "transfers_pinning_mode_entered",
    buildPayload({
      artworkId,
      sessionId,
      hasExistingPins,
      ...(initialMessage && { initialMessage })
    })
  );
};

export const trackPinsSessionCancelled = ({
  artworkId,
  sessionId,
  pinsCount,
  readyPinsCount
}: BaseAnalyticsProps & {
  pinsCount: number;
  readyPinsCount: number;
}): void => {
  submitDtfEvent(
    "pins_session_cancelled",
    buildPayload({
      artworkId,
      sessionId,
      pinsCount,
      readyPinsCount
    })
  );
};

export const trackPinsSessionCancellationAborted = ({
  artworkId,
  sessionId,
  pinsCount
}: BaseAnalyticsProps & { pinsCount: number }): void => {
  submitDtfEvent(
    "pins_session_cancellation_aborted",
    buildPayload({
      artworkId,
      sessionId,
      pinsCount
    })
  );
};

export const trackPinsSessionEditCancelModalConfirmed = ({
  sessionId,
  newPinsCount
}: {
  sessionId: string;
  newPinsCount: number;
}): void => {
  submitDtfEvent(
    "pins_session_edit_cancel_modal_confirmed",
    buildPayload({
      sessionId,
      newPinsCount
    })
  );
};

export const trackPinsSessionEditCancelModalStayed = ({
  sessionId,
  newPinsCount
}: {
  sessionId: string;
  newPinsCount: number;
}): void => {
  submitDtfEvent(
    "pins_session_edit_cancel_modal_stayed",
    buildPayload({
      sessionId,
      newPinsCount
    })
  );
};

export const trackPinsSessionEditCancelFailed = ({
  sessionId,
  newPinsCount,
  errorMessage
}: {
  sessionId: string;
  newPinsCount: number;
  errorMessage: string;
}): void => {
  submitDtfEvent(
    "pins_session_edit_cancel_failed",
    buildPayload({
      sessionId,
      newPinsCount,
      errorMessage
    })
  );
};

export const trackTransfersPinningSessionCompleted = ({
  artworkId,
  sessionId,
  pinsCount,
  readyPinsCount,
  summary
}: BaseAnalyticsProps & {
  pinsCount: number;
  readyPinsCount: number;
  summary: string | null;
}): void => {
  submitDtfEvent(
    "transfers_pinning_session_completed",
    buildPayload({
      artworkId,
      sessionId,
      pinsCount,
      readyPinsCount,
      summary
    })
  );
};

export const trackTransfersPinningSessionCompletionFailed = ({
  artworkId,
  sessionId,
  errorMessage
}: BaseAnalyticsProps & { errorMessage: string }): void => {
  submitDtfEvent(
    "transfers_pinning_session_completion_failed",
    buildPayload({
      artworkId,
      sessionId,
      errorMessage
    })
  );
};

export const trackTransfersPinCreated = ({
  artworkId,
  sessionId,
  pinId
}: PinAnalyticsProps): void => {
  submitDtfEvent(
    "transfers_pin_created",
    buildPayload({
      artworkId,
      sessionId,
      pinId
    })
  );
};

export const trackTransfersPinMessageSubmitted = ({
  artworkId,
  sessionId,
  pinId,
  isFirstMessage,
  turnIndex,
  userMessagesCount,
  aiMessagesCount,
  clarifications
}: SessionCountProps & {
  isFirstMessage: boolean;
  turnIndex: number;
  clarifications: Array<{ question: string; value: string; option_type: string }>;
}): void => {
  submitDtfEvent(
    "transfers_pin_message_submitted",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      isFirstMessage,
      turnIndex,
      userMessagesCount,
      aiMessagesCount,
      clarifications
    })
  );
};

export const trackTransfersPinClarificationClicked = ({
  artworkId,
  sessionId,
  pinId,
  optionValue,
  turnIndex,
  userMessagesCount,
  aiMessagesCount,
  optionType
}: SessionCountProps & {
  optionValue: string;
  turnIndex: number;
  optionType: string;
}): void => {
  submitDtfEvent(
    "transfers_pin_clarification_clicked",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      optionValue,
      turnIndex,
      userMessagesCount,
      aiMessagesCount,
      optionType
    })
  );
};

export const trackTransfersPinReady = ({
  artworkId,
  sessionId,
  pinId,
  userMessagesCount,
  aiMessagesCount
}: SessionCountProps): void => {
  submitDtfEvent(
    "transfers_pin_ready",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      userMessagesCount,
      aiMessagesCount
    })
  );
};

export const trackTransfersPinUnsupported = ({
  artworkId,
  sessionId,
  pinId,
  userMessagesCount,
  aiMessagesCount
}: SessionCountProps): void => {
  submitDtfEvent(
    "transfers_pin_unsupported",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      userMessagesCount,
      aiMessagesCount
    })
  );
};

export const trackTransfersPinNeedsClarification = ({
  artworkId,
  sessionId,
  pinId,
  optionsCount,
  questionType,
  userMessagesCount,
  aiMessagesCount
}: SessionCountProps & {
  optionsCount: number;
  questionType: string;
}): void => {
  submitDtfEvent(
    "transfers_pin_needs_clarification",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      optionsCount,
      ...(questionType && { questionType }),
      userMessagesCount,
      aiMessagesCount
    })
  );
};

export const trackTransfersPinError = ({
  artworkId,
  sessionId,
  pinId,
  errorType,
  userMessagesCount,
  aiMessagesCount
}: SessionCountProps & { errorType: string }): void => {
  submitDtfEvent(
    "transfers_pin_error",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      errorType,
      userMessagesCount,
      aiMessagesCount
    })
  );
};

export const trackTransfersPinSelected = ({
  artworkId,
  sessionId,
  pinId,
  previousPinId,
  pinState
}: PinAnalyticsProps & {
  previousPinId: string | null;
  pinState: string;
}): void => {
  submitDtfEvent(
    "transfers_pin_selected",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      ...(previousPinId && { previousPinId }),
      pinState
    })
  );
};

export const trackTransfersPinDeleted = ({
  artworkId,
  sessionId,
  pinId,
  pinState,
  hadMessages
}: PinAnalyticsProps & {
  pinState: string;
  hadMessages: boolean;
}): void => {
  submitDtfEvent(
    "transfers_pin_deleted",
    buildPayload({
      artworkId,
      sessionId,
      pinId,
      pinState,
      hadMessages
    })
  );
};

export const trackTransfersPinDeleteFailed = ({
  artworkId,
  sessionId,
  pinId
}: PinAnalyticsProps): void => {
  submitDtfEvent(
    "transfers_pin_delete_failed",
    buildPayload({
      artworkId,
      sessionId,
      pinId
    })
  );
};

export const trackPinSessionEscapeHatchClicked = ({
  triggerType,
  pinCount,
  messageCount
}: EscapeHatchAnalyticsProps): void => {
  submitDtfEvent(
    "pin_session_escape_hatch_clicked",
    buildPayload({
      triggerType,
      pinCount,
      messageCount
    })
  );
};

export const trackPinSessionEscapeHatchConfirmed = ({
  triggerType,
  pinCount,
  messageCount
}: EscapeHatchAnalyticsProps): void => {
  submitDtfEvent(
    "pin_session_escape_hatch_confirmed",
    buildPayload({
      triggerType,
      pinCount,
      messageCount
    })
  );
};

export const trackPinSessionEscapeHatchDismissed = ({
  triggerType,
  pinCount,
  messageCount
}: EscapeHatchAnalyticsProps): void => {
  submitDtfEvent(
    "pin_session_escape_hatch_dismissed",
    buildPayload({
      triggerType,
      pinCount,
      messageCount
    })
  );
};

export const trackTransfersPinningSessionEntryInput = ({
  artworkId,
  inputValue,
  userType
}: {
  artworkId: string;
  inputValue: string;
  userType?: string;
}): void => {
  submitDtfEvent(
    "pinning_session_entry_input",
    buildPayload({
      artworkId,
      placement: null,
      input: inputValue,
      ...(userType && { userType })
    })
  );
};

export const trackPinSessionsGenPreviewBeforeClicked = ({
  artworkId,
  sessionId,
  generatedImageUrl
}: BaseAnalyticsProps & { generatedImageUrl: string }): void => {
  submitDtfEvent(
    "pin_sessions_gen_preview_before_clicked",
    buildPayload({ artworkId, sessionId, generatedImageUrl })
  );
};

export const trackPinSessionsGenPreviewAfterClicked = ({
  artworkId,
  sessionId,
  generatedImageUrl
}: BaseAnalyticsProps & { generatedImageUrl: string }): void => {
  submitDtfEvent(
    "pin_sessions_gen_preview_after_clicked",
    buildPayload({ artworkId, sessionId, generatedImageUrl })
  );
};

export const trackPinSessionsGenConfirmed = ({
  artworkId,
  sessionId,
  pinId
}: PinAnalyticsProps): void => {
  submitDtfEvent("pin_sessions_gen_confirmed", buildPayload({ artworkId, sessionId, pinId }));
};

export const trackPinSessionsGenDiscarded = ({
  artworkId,
  sessionId,
  pinId
}: PinAnalyticsProps): void => {
  submitDtfEvent("pin_sessions_gen_discarded", buildPayload({ artworkId, sessionId, pinId }));
};

export const trackPinSessionGeneratedImageShown = ({
  artworkId,
  sessionId,
  generatedImageUrl,
  pinId
}: PinAnalyticsProps & { generatedImageUrl: string }): void => {
  submitDtfEvent(
    "pin_session_generated_image_shown",
    buildPayload({ artworkId, sessionId, generatedImageUrl, pinId })
  );
};

export const trackTransfersArtistEditEscalationShown = ({
  artworkId,
  sessionId,
  pinId
}: PinAnalyticsProps): void => {
  submitDtfEvent(
    "transfers_artist_edit_escalation_shown",
    buildPayload({ artworkId, sessionId, pinId })
  );
};
